import { useEffect, useMemo, useState, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { fetchWithAuth } from '../utils/fetchWithAuth';
import InterventionPointIssues from './InterventionPointIssues';
import InterventionPointDeployments from './InterventionPointDeployments';
import InterventionPointsDetail from './InterventionPointsDetail';
import AppContext from '../contexts/AppContext';

const MAX_RESULTS = 25;

const RequestTypeSelectionModal = ({
  isOpen,
  requestTypes,
  requestTypesLoading,
  furnitureLabel,
  bottomOffset = 0,
  onClose,
  onSelect,
}) => {
  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-[200] flex items-end bg-black/40 p-0 sm:items-center sm:justify-center sm:p-4"
      style={{ paddingBottom: bottomOffset }}
      onClick={onClose}
    >
      <div
        className="w-full overflow-hidden rounded-t-2xl bg-white shadow-xl sm:max-w-lg sm:rounded-2xl"
        style={{ maxHeight: `calc(85vh - ${bottomOffset}px)` }}
        onClick={(event) => event.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-4 border-b border-gray-200 px-4 py-3">
          <div>
            <h3 className="text-base font-semibold text-gray-900">Choisir un type de demande</h3>
            {furnitureLabel && (
              <p className="mt-1 text-sm text-gray-600">{furnitureLabel}</p>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="inline-flex h-8 w-8 items-center justify-center rounded-full text-gray-500 hover:bg-gray-100 hover:text-gray-900"
            aria-label="Fermer"
          >
            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="max-h-[70vh] overflow-y-auto py-2">
          {requestTypesLoading ? (
            <div className="px-4 py-3 text-sm text-gray-500">Chargement...</div>
          ) : requestTypes.length === 0 ? (
            <div className="px-4 py-3 text-sm text-gray-500">Aucun type de demande</div>
          ) : (
            requestTypes.map((requestType) => (
              <button
                key={requestType.id}
                type="button"
                onClick={() => onSelect(requestType.id)}
                className="block w-full px-4 py-3 text-left text-sm text-gray-900 hover:bg-gray-50"
              >
                {requestType.name}
              </button>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

const matchesCriteria = (attr, crit) => {
  const name = attr?.name || '';
  const id = String(attr?.id || '');
  return (crit?.name && name === crit.name) || (crit?.id && id === String(crit.id));
};

const resolveAttribute = (config, keyOrAttribute) => {
  if (!keyOrAttribute || typeof keyOrAttribute !== 'string') {
    return keyOrAttribute;
  }

  const attribute = config?.attributes?.[keyOrAttribute];
  if (!attribute) return null;

  return {
    key: keyOrAttribute,
    label: attribute.label || attribute.name,
    ...attribute,
  };
};

const resolveAttributes = (config, keys) => {
  return (keys || [])
    .map(key => resolveAttribute(config, key))
    .filter(Boolean);
};

export default function InterventionPoints() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [points, setPoints] = useState([]); // [{ id, key, label }]
  const [query, setQuery] = useState('');
  const [selected, setSelected] = useState(null); // { id, key, label }
  const [detail, setDetail] = useState(null); // payload from /intervention-points/:key
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailCache, setDetailCache] = useState({}); // key -> detail
  const [detailLoadingKeys, setDetailLoadingKeys] = useState({}); // key -> boolean
  const [requestTypes, setRequestTypes] = useState([]);
  const [requestTypesLoading, setRequestTypesLoading] = useState(false);
  const [requestSelector, setRequestSelector] = useState(null);
  const { isInWebView, serviceDesk } = useContext(AppContext);
  const serviceDeskConfig = serviceDesk?.config;

  const ATTR_CONFIG = serviceDeskConfig?.interventionPointConfig || { title: [], compact: [], details: [], sections: { mobiliers: {} } };
  const sectorAttribute = resolveAttribute(ATTR_CONFIG, 'sector');

  useEffect(() => {
    let cancelled = false;
    async function loadList() {
      try {
        setLoading(true);
        setError('');
        const res = await fetchWithAuth('intervention-points');
        const body = await res.json();
        const values = body?.data?.values || body?.values || [];

        const mapped = Array.isArray(values)
          ? values.map((v) => ({
              id: String(v.id || v.objectId || v.key || ''),
              key: String(v.key || v.objectKey || v.id || ''),
              label: String(v.label || v.name || v.displayName || v.key || ''),
              sap_code: String(v.sap_code || ''),
            }))
          : [];
        if (!cancelled) setPoints(mapped);
      } catch (e) {
        if (!cancelled) setError(e?.message || 'Erreur inconnue');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    loadList();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    let cancelled = false;
    async function loadRequestTypes() {
      try {
        setRequestTypesLoading(true);
        const res = await fetchWithAuth('form-types');
        const body = await res.json();
        if (!cancelled) setRequestTypes(body?.values || []);
      } catch (e) {
        if (!cancelled) setError(e?.message || 'Erreur inconnue');
      } finally {
        if (!cancelled) setRequestTypesLoading(false);
      }
    }

    loadRequestTypes();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    setRequestSelector(null);
  }, [selected?.id]);

  const filtered = useMemo(() => {
    const q = String(query || '').toLowerCase().trim();

    if (!q) return points;
    return points.filter((p) =>
      p.label.toLowerCase().includes(q) ||
      p.sap_code?.toLowerCase().includes(q)
    );
  }, [points, query]);

  const ensureDetail = async (pointObjectId, { includeFurnitureBrands = false } = {}) => {
    if (!pointObjectId) return null;
    const cacheKey = `${pointObjectId}:${includeFurnitureBrands ? 'withFurnitureBrands' : 'base'}`;
    if (detailCache[cacheKey]) return detailCache[cacheKey];
    if (detailLoadingKeys[cacheKey]) return null;
    try {
      setDetailLoadingKeys(prev => ({ ...prev, [cacheKey]: true }));
      const params = includeFurnitureBrands ? '?includeFurnitureBrands=1' : '';
      const res = await fetchWithAuth(`intervention-points/${encodeURIComponent(pointObjectId)}/attributes${params}`);
      const body = await res.json();
      const d = body?.data || body || null;
      setDetailCache(prev => ({ ...prev, [cacheKey]: d }));
      return d;
    } catch (e) {
      setError(e?.message || 'Erreur inconnue');
      return null;
    } finally {
      setDetailLoadingKeys(prev => ({ ...prev, [cacheKey]: false }));
    }
  };

  const onSelectPoint = async (opt) => {
    if (!opt) {
      setSelected(null);
      setDetail(null);
      return;
    }
    setSelected(opt);
    setDetail(null);
    try {
      setDetailLoading(true);
      const d = await ensureDetail(opt.id, { includeFurnitureBrands: true });
      setDetail(d);
    } catch (e) {
      setError(e?.message || 'Erreur inconnue');
    } finally {
      setDetailLoading(false);
    }
  };

  const getAttrValues = (detailObj, matcher) => {
    try {
      const items = Array.isArray(detailObj)
        ? detailObj
        : (Array.isArray(detailObj?.attributes) ? detailObj.attributes : []);

      for (const item of items) {
        const name = item?.objectTypeAttribute?.name || item?.name || '';
        const id = String(item?.objectTypeAttributeId || item?.id || '');
        const valuesArray = (item?.objectAttributeValues || item?.values || []);
        const mappedValues = valuesArray
          .map(v => {
            if (v?.referencedType && v?.referencedObject) {
              return v.referencedObject.label || v.displayValue || v.value;
            }
            return v?.displayValue || v?.value;
          })
          .filter(Boolean);

        if (typeof matcher === 'string') {
          if (name === matcher || id === matcher) return mappedValues;
        } else if (typeof matcher === 'function') {
          if (matcher({ name, id, item })) return mappedValues;
        }
      }
      return [];
    } catch (_) {
      return [];
    }
  };

  const getAttrObjects = (detailObj, matcher) => {
    try {
      const items = Array.isArray(detailObj)
        ? detailObj
        : (Array.isArray(detailObj?.attributes) ? detailObj.attributes : []);

      for (const item of items) {
        const name = item?.objectTypeAttribute?.name || item?.name || '';
        const id = String(item?.objectTypeAttributeId || item?.id || '');
        const valuesArray = (item?.objectAttributeValues || item?.values || []);

        if (typeof matcher === 'string') {
          if (name !== matcher && id !== matcher) continue;
        } else if (typeof matcher === 'function' && !matcher({ name, id, item })) {
          continue;
        }

        return valuesArray
          .map(v => v?.referencedObject || v)
          .map(v => {
            const label = String(v?.label || v?.name || v?.displayValue || v?.value || '');

            return {
              key: String(v?.objectKey || v?.key || label),
              label,
              brand: v?.brand?.label || 'Sans marque',
            };
          })
          .filter(v => v.key && v.label);
      }

      return [];
    } catch (_) {
      return [];
    }
  };

  // Helper using local getAttrValues; supports criteria with name or id
  const valuesFor = (detailObj, criteria) => {
    const matchFn = ({ name, id }) => matchesCriteria({ name, id }, criteria);
    return getAttrValues(detailObj, matchFn);
  };

  const titleAttributes = resolveAttributes(ATTR_CONFIG, ATTR_CONFIG.title);
  const compactAttributes = resolveAttributes(ATTR_CONFIG, ATTR_CONFIG.compact);
  const detailAttributes = resolveAttributes(ATTR_CONFIG, ATTR_CONFIG.details);
  const furnitureAttribute = resolveAttribute(ATTR_CONFIG, ATTR_CONFIG.sections?.mobiliers);

  const groupFurnituresByBrand = (furnitures) => {
    return furnitures.reduce((groups, furniture) => {
      const brand = furniture.brand || 'Sans marque';
      const existingGroup = groups.find(group => group.brand === brand);

      if (existingGroup) {
        existingGroup.furnitures.push(furniture);
        return groups;
      }

      return [...groups, { brand, furnitures: [furniture] }];
    }, []);
  };

  const selectedObjectKey = detail
    ? (getAttrValues(detail, ({ name, id }) => name === 'Key' || id === '189')[0] || selected?.key)
    : selected?.key;

  const openRequest = (requestTypeId, furnitureKey = null) => {
    if (!requestTypeId || !selectedObjectKey) return;

    const params = new URLSearchParams({ interventionPoint: selectedObjectKey });

    if (furnitureKey) {
      params.set('furniture', furnitureKey);
    }

    navigate(`/request/${requestTypeId}?${params.toString()}`);
  };

  const openRequestSelector = (furniture = null) => {
    setRequestSelector({
      furnitureKey: furniture?.key || null,
      furnitureLabel: furniture?.label || null,
    });
  };

  const handleRequestTypeSelect = (requestTypeId) => {
    openRequest(requestTypeId, requestSelector?.furnitureKey || null);
    setRequestSelector(null);
  };

  // Prefetch details for the first results to show sector and furniture count on cards
  useEffect(() => {
    if (filtered.length > MAX_RESULTS) return;

    const prefetch = async () => {
      const first = filtered.slice(0, MAX_RESULTS);
      for (const p of first) {
        const cacheKey = `${p.id}:base`;
        if (!detailCache[cacheKey] && !detailLoadingKeys[cacheKey]) {
          ensureDetail(p.id);
        }
      }
    };
    prefetch();
  }, [filtered, detailCache, detailLoadingKeys]);

  return (
    <div className="h-full flex flex-col">
      <div className="flex-1 overflow-y-auto min-h-0">
        <div className="container mx-auto py-6">
          {/* <h1 className="mb-4 text-2xl font-bold text-black">Points d'intervention</h1> */}

          {loading ? (
            <div className="flex justify-center py-12">
              <div className="h-14 w-14 animate-spin rounded-full border-b-2 border-black"></div>
            </div>
          ) : error ? (
            <div className="rounded border border-red-200 bg-red-50 p-3 text-sm text-red-700">{error}</div>
          ) : (
            <div className="rounded border border-gray-200 bg-white p-4">
              <div className="mb-4">
                <label className="mb-1 block text-sm font-medium text-gray-700">Rechercher un magasin</label>
                  <div className="relative">
                    <input
                      type="text"
                      value={query}
                      onChange={(e) => setQuery(e.target.value)}
                      placeholder="Tapez le nom ou code SAP du magasin…"
                      className="mt-1 block w-full rounded-md border p-2 pr-10 text-sm border-gray-300 focus:border-blue-500 focus:ring-blue-500"
                    />
                    {query.length > 0 && (
                      <button
                        type="button"
                        onClick={() => { setQuery(''); setSelected(null); setDetail(null); }}
                        className="absolute right-2 top-1/2 -translate-y-1/2 inline-flex items-center justify-center w-6 h-6 rounded-full bg-gray-200 text-gray-600 hover:bg-gray-300"
                        aria-label="Effacer la recherche"
                        title="Effacer"
                      >
                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    )}
                  </div>
              </div>

              <div className="mb-2">
                {filtered.length > 0 && !selected && (
                  <p className="text-xs text-gray-500">{filtered.length} magasins</p>
                )}
              </div>

              {query.length > 0 && !selected && filtered.length > MAX_RESULTS && (
                <div className="rounded border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
                  Trop de résultats ({filtered.length}). Veuillez affiner votre recherche.
                </div>
              )}

              {query.length === 0 && (
                <div className="rounded border border-gray-200 bg-gray-50 p-3 text-sm text-gray-900">
                Veuillez entrer un nom ou code SAP de magasin pour afficher les résultats.
                </div>
              )}

              {query.length > 0 && !selected && filtered.length === 0 && (
                <div className="rounded border border-gray-200 bg-gray-50 p-3 text-sm text-gray-900">
                  Aucun magasin ne correspond à votre recherche.
                </div>
              )}

              {!selected && filtered.length > 0 && filtered.length <= MAX_RESULTS && (
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
                  {filtered.slice(0, MAX_RESULTS).map((p) => {
                    const d = detailCache[`${p.id}:base`];
                    const secteurs = d ? getAttrValues(d, ({ name, id }) => matchesCriteria({ name, id }, sectorAttribute)) : [];
                    const mobiliers = d ? getAttrValues(d, ({ name, id }) => matchesCriteria({ name, id }, furnitureAttribute)) : [];
                    return (
                      <button
                        key={p.id}
                        type="button"
                        onClick={() => onSelectPoint(p)}
                        className="text-left rounded-lg border border-gray-200 p-3 hover:border-black transition-colors bg-white"
                      >
                        <div className="text-sm font-medium text-gray-900 truncate">{p.label}</div>
                        <div className="mt-1 text-xs text-gray-600">
                          {p.sap_code ? `Code SAP: ${p.sap_code}` : '—'}
                        </div>
                        <div className="text-xs text-gray-600">
                          Secteur: {secteurs.length ? secteurs.join(', ') : (detailLoadingKeys[`${p.id}:base`] ? 'Chargement…' : '—')}
                        </div>
                        <div className="text-xs text-gray-600">
                          Mobiliers: {mobiliers.length ? mobiliers.length : (detailLoadingKeys[`${p.id}:base`] ? '…' : '0')}
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}

              {detailLoading && (
                <div className="flex justify-center py-6">
                  <div className="h-6 w-6 animate-spin rounded-full border-b-2 border-primary"></div>
                </div>
              )}

              {detail && (
                <div className="mt-4 space-y-4">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <h2 className="text-lg font-semibold text-black">
                        {titleAttributes.map(field => valuesFor(detail, field)[0]).find(Boolean) || selected?.label}
                      </h2>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => openRequestSelector()}
                        disabled={!selectedObjectKey || requestTypesLoading}
                        className="inline-flex items-center justify-center gap-1 rounded-lg border border-black bg-black px-3 py-2 text-xs font-medium text-white hover:bg-gray-800 disabled:cursor-not-allowed disabled:border-gray-300 disabled:bg-gray-300"
                      >
                        <span>Ouvrir une demande</span>
                      </button>

                      <button
                        type="button"
                        onClick={() => { setSelected(null); setDetail(null); }}
                        className="inline-flex items-center justify-center w-8 h-8 rounded-full border border-gray-300 bg-white text-gray-700 hover:bg-gray-50"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                  </div>

                  <div className="flex flex-wrap items-center gap-2">
                    {compactAttributes.map((field) => {
                      const vals = valuesFor(detail, field).join(', ');
                      return (
                        <span key={field.key} className="inline-flex items-center rounded-full border border-gray-200 bg-gray-50 px-2.5 py-1 text-xs text-gray-800">
                          <span className="mr-1 text-gray-500">{field.label}:</span>
                          {vals || '—'}
                        </span>
                      );
                    })}
                  </div>

                  {/* Details - now displayed before Mobiliers, compact style and not collapsible */}
                  <InterventionPointsDetail detail={detail} orderList={detailAttributes} />

                  <div className="rounded border border-gray-200">
                    <div className="border-b px-3 py-2 text-sm font-medium text-gray-700">{furnitureAttribute.label}</div>
                    <div className="p-3">
                      {(() => {
                        const furnitures = getAttrObjects(detail, ({ name, id }) => matchesCriteria({ name, id }, furnitureAttribute));
                        const groups = groupFurnituresByBrand(furnitures);

                        if (groups.length === 0) {
                          return <div className="text-sm text-gray-500">Aucun mobilier trouvé</div>;
                        }

                        return (
                          <div className="overflow-visible rounded-lg border border-gray-200">
                            <table className="w-full divide-y divide-gray-200 text-sm">
                              <tbody className="divide-y divide-gray-200">
                                {groups.map((group) => (
                                  <tr key={group.brand} className="align-top">
                                    <th className="w-32 bg-gray-50 px-3 py-3 text-left text-xs font-semibold text-gray-700">
                                      {group.brand}
                                    </th>
                                    <td className="px-3 py-2">
                                      <div className="flex flex-wrap gap-2">
                                        {group.furnitures.map((furniture) => {
                                          return (
                                            <button
                                              key={furniture.key}
                                              type="button"
                                              onClick={() => openRequestSelector(furniture)}
                                              className="inline-flex items-center rounded-full border border-gray-200 bg-gray-50 py-1 pl-2.5 pr-2 text-xs text-gray-800 hover:border-black hover:bg-white focus:outline-none focus:ring-2 focus:ring-black focus:ring-offset-1"
                                              aria-label={`Ouvrir une demande pour ${furniture.label}`}
                                            >
                                              <span>{furniture.label}</span>
                                              <svg className="ml-1.5 h-3 w-3 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 5v14M5 12h14" />
                                              </svg>
                                            </button>
                                          );
                                        })}
                                      </div>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        );
                      })()}
                    </div>
                  </div>



                  {/* Issues history for the selected intervention point */}
                  {(() => {
                    const keyFromAttr = getAttrValues(detail, ({ name, id }) => name === 'Key' || id === '189')[0];
                    const objectKey = keyFromAttr || selected?.key;
                    return (
                      <InterventionPointIssues objectKey={objectKey} />
                    );
                  })()}

                  {/* Deployments history - placeholder */}
                  <InterventionPointDeployments />

                  {/* Placeholder for tickets history (requires backend endpoint by intervention point) */}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
      <RequestTypeSelectionModal
        isOpen={Boolean(requestSelector)}
        requestTypes={requestTypes}
        requestTypesLoading={requestTypesLoading}
        furnitureLabel={requestSelector?.furnitureLabel}
        bottomOffset={isInWebView ? 0 : 75}
        onClose={() => setRequestSelector(null)}
        onSelect={handleRequestTypeSelect}
      />
    </div>
  );
}


